import { IonCard, IonIcon, IonSkeletonText } from "@ionic/react";
import { banOutline } from "ionicons/icons";
import { useDeviceQuery } from "../query/deviceQuery";
import { useDeviceEventsQuery } from "../query/deviceEventsQuery";
import { useDeviceTransitPolicyQuery } from "../query/deviceTransitPolicyQuery";
import { lockIconNameForTransitPolicy } from './DeviceTransitPolicyTile';
import Timestamp from './Timestamp';
import styles from './DeviceHeroCard.module.css';
import { useTranslation } from 'react-i18next';

interface DeviceHeroCardProps {
    deviceId: string;
}

/**
 * Wide device card used on the device list: identity and status on the left,
 * door policy and last event pinned to the bottom, product photo bleeding off
 * the right edge over a connectivity-tinted wash.
 */
const DeviceHeroCard: React.FC<DeviceHeroCardProps> = ({ deviceId }) => {
    const { t } = useTranslation('components');
    const { t: tPages } = useTranslation('pages');
    const deviceQuery = useDeviceQuery(deviceId);
    const deviceEventsQuery = useDeviceEventsQuery(deviceId, undefined, 1);
    const deviceTransitPolicyQuery = useDeviceTransitPolicyQuery(deviceQuery.data?.deviceTransitPolicyId);

    const device = deviceQuery.data;
    const connected = !!device?.connectivity?.connected;
    const lastEvent = deviceEventsQuery.data?.pages?.[0]?.[0];

    if (deviceQuery.isError) {
        return <div className={styles.wrapper}>
            <IonCard className={`${styles.card} ${styles.offline}`} button onClick={() => deviceQuery.refetch()}>
                <img className={styles.photo} src="/assets/device/onlycat-device.png" alt={t('device_list_item.onlycat_device')} />
                <div className={styles.body}>
                    <div className={styles.name}>
                        <h2>{deviceId}</h2>
                    </div>
                    <div className={styles.footer}>
                        <span className={`${styles.pill} ${styles.dangerPill}`}>
                            <IonIcon icon={banOutline} />
                            <span>{t('device_list_item.unavailable')}</span>
                        </span>
                    </div>
                </div>
            </IonCard>
        </div>
    }

    return (
        <div className={styles.wrapper}>
            <IonCard className={`${styles.card} ${connected ? '' : styles.offline}`} button routerLink={`/devices/${deviceId}`}>
                <img className={styles.photo} src="/assets/device/onlycat-device.png" alt={t('device_list_item.onlycat_device')} />
                <div className={styles.body}>
                    <div className={styles.status}>
                        <span className={styles.statusDot} />
                        {connected ? t('device_list_item.online') : t('device_list_item.offline')}
                    </div>
                    <div className={styles.name}>
                        <h2>{deviceQuery.isLoading
                            ? <IonSkeletonText animated={true} style={{ width: '140px' }} />
                            : (device?.description || device?.deviceId)}
                        </h2>
                    </div>
                    <div className={styles.footer}>
                        <span className={styles.pill}>
                            <IonIcon src={`/assets/lock/${lockIconNameForTransitPolicy(deviceTransitPolicyQuery.data?.transitPolicy)}.svg`} />
                            <span>{deviceTransitPolicyQuery.data?.name
                                ?? (deviceQuery.isSuccess && !device?.deviceTransitPolicyId ? tPages('device.not_set') : '\u00a0')}</span>
                        </span>
                        <span className={styles.lastEvent}>
                            {t('device_list_item.last_event')}
                            {' · '}
                            {lastEvent?.timestamp ? <Timestamp timestamp={lastEvent.timestamp} short /> : (deviceEventsQuery.isSuccess ? '-' : '')}
                        </span>
                    </div>
                </div>
            </IonCard>
        </div>
    );
};

export default DeviceHeroCard;
